import React, { useRef, useLayoutEffect, ReactNode } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'
import { SplitText } from 'gsap/dist/SplitText'
import {
  Container,
  PreTitle,
  Title,
  Paragraph,
  MobileNavigationWrapper,
} from './styled'
import { useContentInProp } from '@/hooks/useContentInProp'
import { TextNavigationLink } from '../TextNavigationLink'

type Link = {
  url: string
  link: string
  variant?: string
  linkType?: 'link' | 'button'
  buttonCallback?: () => void
}

interface Props {
  pretitle?: string
  title?: string
  text?: string
  links?: Link[]
  link?: string
  link2?: string
  link2Mobile?: boolean
  url?: string
  url2?: string
  variant?: string
  textAnimationType?: 'mask' | 'clip'
  useTextAnimation?: boolean
  inProp?: boolean
  isMobile?: boolean
  withIndicators?: ReactNode
  padding?: string
}

export const Text: React.FC<Props> = ({
  pretitle,
  title,
  text,
  links,
  variant,
  useTextAnimation,
  children,
  isMobile,
  withIndicators,
  padding,
}) => {
  const inProp = useContentInProp()
  const stRef = useRef(null)
  const tweenRef = useRef(null)
  const containerRef = useRef(null)
  const pretitleRef = useRef(null)
  const titleRef = useRef(null)
  const paragraphRef = useRef(null)

  // useLayoutEffect(() => {
  //   if (containerRef.current) {
  //     containerRef.current.style.opacity = 0
  //   }
  // }, [containerRef.current])

  useLayoutEffect(() => {
    if (tweenRef.current) {
      tweenRef.current.kill()
    }

    if (stRef.current) {
      stRef.current.revert()
    }

    if (!inProp) {
      return
    }

    const tl = gsap.timeline({})
    const currentReference = containerRef.current

    if (currentReference) {
      containerRef.current.style.opacity = 0
    }

    tweenRef.current = gsap.delayedCall(0.5, () => {
      if (containerRef.current) {
        containerRef.current.style.opacity = 1
      }
      if (titleRef.current) {
        stRef.current = new SplitText(titleRef.current, {
          linesClass: 'line',
          type: 'lines',
        })
        const { lines } = stRef.current
        if (pretitle) {
          const pretitleChars = new SplitText(pretitleRef.current, {
            charsClass: 'char',
            type: 'chars',
          }).chars
          tl.fromTo(
            pretitleChars,
            0.3,
            {
              y: 16,
            },
            {
              force3D: true,
              ease: 'sine.out',
              stagger: 0.02,
              y: 0,
            },
            0.4,
          )
        }
        const charsOfLines = lines.map(
          (line) =>
            new SplitText(line, {
              charsClass: 'char',
              type: 'words,chars',
            }).chars,
        )
        charsOfLines.forEach((chars) => {
          tl.fromTo(
            chars,
            0.5,
            {
              y: 115,
            },
            {
              force3D: true,
              ease: 'sine.out',
              stagger: 0.005,
              y: 0,
            },
            0.25,
          )
        })
        if (paragraphRef.current) {
          tl.fromTo(
            paragraphRef.current,
            0.55,
            {
              opacity: 0,
              y: 20,
            },
            {
              force3D: true,
              ease: 'sine.out',
              opacity: 1,
              y: 0,
            },
            0.75,
          )
        }
      }
      if (paragraphRef.current && !titleRef.current) {
        tl.fromTo(
          paragraphRef.current,
          1,
          {
            opacity: 0,
            y: 20,
          },
          {
            force3D: true,
            ease: 'sine.out',
            opacity: 1,
            y: 0,
          },
          0.4,
        )
      }
    })
  }, [inProp])

  let content = (
    <>
      {withIndicators && withIndicators}
      {links?.map((linkEntry, index) => (
        <TextNavigationLink
          key={linkEntry.url}
          variant={variant}
          delay={index / 3}
          time={1}
          url={linkEntry.url}
          useAnimation={useTextAnimation}
          text={linkEntry.link}
          styleVariant={linkEntry.variant}
          linkType={linkEntry.linkType}
        />
      ))}
    </>
  )
  const mobileContent = (
    <>
      {withIndicators && withIndicators}

      <MobileNavigationWrapper>
        {links?.map((linkEntry, index) => (
          <TextNavigationLink
            key={linkEntry.url}
            variant={variant}
            delay={index / 3}
            time={1}
            url={linkEntry.url}
            useAnimation={useTextAnimation}
            text={linkEntry.link}
            styleVariant={linkEntry.variant}
            linkType={linkEntry.linkType}
            buttonCallback={linkEntry.buttonCallback}
          />
        ))}
      </MobileNavigationWrapper>
    </>
  )

  if (isMobile) {
    content = mobileContent
  }

  return (
    <>
      {inProp && (
        <Container padding={padding} ref={containerRef}>
          {pretitle && (
            <PreTitle>
              <span ref={pretitleRef}>{pretitle}</span>
            </PreTitle>
          )}
          {title && (
            <Title variant="page-title" ref={titleRef}>
              {title}
            </Title>
          )}
          {text && <Paragraph ref={paragraphRef}>{text}</Paragraph>}
          {children || content}
        </Container>
      )}
    </>
  )
}

Text.defaultProps = {
  text: null,
  title: null,
  useTextAnimation: true,
  variant: 'dark',
  pretitle: null,
  links: null,
  isMobile: false,
  withIndicators: null,
  padding: '0 0 0 0',
}

Text.propTypes = {
  pretitle: PropTypes.string,
  text: PropTypes.string,
  title: PropTypes.string,
  useTextAnimation: PropTypes.bool,
  variant: PropTypes.string,
  isMobile: PropTypes.bool,
  withIndicators: PropTypes.node,
  links: PropTypes.array,
  padding: PropTypes.string,
}
